import React, { useCallback, useEffect, useState } from "react"
import useExitPrompt from "hooks/useExitPrompt"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { AnalyticsService } from "services/AnalyticsService"
import { GeolocationService, ProfileOfferService } from "services/api-service"
import { SentryService } from "services/SentryService"
import { updateProfile } from "store/profile"
import { StyledComponentsProvider } from "ui/providers/styled-components"

import AddOfferAdditionalInfo from "./AddOfferAdditionalInfo"
import AddOfferBasicInfo from "./AddOfferBasicInfo"
import AddOfferPlan from "./AddOfferPlan"
import AddOfferSalaryInfo from "./AddOfferSalaryInfo"
import AddOfferSummary from "./AddOfferSummary"

const AddOffer = () => {
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [showExitPrompt, setShowExitPrompt] = useExitPrompt(false)

  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(0)
  const [state, setState] = useState<any>({})

  const handleSubmitPlan = useCallback(
    (plan: string, type: string, period: string, code: string) => {
      const newState = {
        ...state,
        plan,
        type,
        period,
        code,
      }
      setStep(1)
      setState(newState)
    },
    [state],
  )

  const handleSetType = useCallback((type: string) => {
    setState((prev: any) => ({
      ...prev,
      type,
    }))
  }, [])

  const handleSubmitBasicInfo = useCallback(
    (category: string, subcategory: string, address: any) => {
      const newState = {
        ...state,
        category,
        subcategory,
        address,
      }
      setStep(2)
      setState(newState)
    },
    [state],
  )

  const handleSubmitSalaryInfo = useCallback(
    (obj: object) => {
      const newState = {
        ...state,
        ...obj,
      }
      setStep(3)
      setState(newState)
    },
    [state],
  )

  const handleSubmitAdditionalInfo = useCallback(
    (obj: object) => {
      const newState = {
        ...state,
        ...obj,
      }
      setStep(4)
      setState(newState)
    },
    [state],
  )

  const handleSubmitSummary = useCallback(
    async (paymentMethod: string) => {
      try {
        setShowExitPrompt(false)
        setLoading(true)
        const localization = await GeolocationService.place(
          `${state.address?.id}`,
          `${state.address?.lang}`,
        )
        const obj: any = {
          plan: state?.plan,
          period: `period_${state?.period}`,
          type: state.type,
          currency: i18n.language === "pl" ? "PLN" : "EUR",
          paymentMethod: paymentMethod,
          code: state.code,
          category: state?.category,
          language: i18n.language,
          subcategory: state?.subcategory,
          name: state.name,
          address: {
            id: localization.id,
            address: localization.address,
            city: localization.city,
            countryCode: localization.country.code,
            provinceCode: localization.province.code,
            postCode: localization.postCode,
            lat: localization.lat,
            lon: localization.lng,
          },
          descriptionDuty: state.descriptions.description1 ?? "",
          descriptionRequirements: state.descriptions.description2 ?? "",
          descriptionWeOffer: state.descriptions.description3 ?? "",
          drivingAreas: state.drivingAreas,
          workTypes: state.workTypes,
          truckTypes: state.truckTypes,
          directions: state.directions,
          workLocations: state.workLocations,
          cast: state.cast,
          experience: state.experience[0],
          languages: state.languages,
          params: state.params ? state.params.filter((e: string) => e !== "uaFriendly") : [],
          legalInformationClause: state.legalInformationClause,
          legalFutureRecruitment: state.switches.legalFutureRecruitment
            ? state.legalFutureRecruitment
            : null,
          applyLink: state.applyLink && state.applyLink !== "" ? state.applyLink : null,
          surveyId: state.survey ? state.survey.id : null,
          uaFriendly: state.params?.includes("uaFriendly"),
        }

        // Build salary structure according to AddOfferType
        const salary: any = {
          contract: null,
          b2b: null,
        }

        if (state.category === "drivers") {
          obj.workTypes = state.selectedWorkTypes || []
        }

        // Build contract salary
        if (state.switchesSalary?.contract) {
          const contractAmounts: any = {}
          const workTypeKeys =
            state.category === "drivers" ? state.selectedWorkTypes || [] : ["all"]

          workTypeKeys.forEach((workType: string) => {
            const payment = state.contractPayments?.[workType]
            if (payment) {
              contractAmounts[workType] = {}
              if (payment.monthlyFrom !== undefined && payment.monthlyFrom !== "")
                contractAmounts[workType].from = Number(payment.monthlyFrom)
              if (payment.monthlyTo !== undefined && payment.monthlyTo !== "")
                contractAmounts[workType].to = Number(payment.monthlyTo)
              if (payment.dailyFrom !== undefined && payment.dailyFrom !== "")
                contractAmounts[workType].dayFrom = Number(payment.dailyFrom)
              if (payment.dailyTo !== undefined && payment.dailyTo !== "")
                contractAmounts[workType].dayTo = Number(payment.dailyTo)
            }
          })

          salary.contract = {
            amounts: contractAmounts,
            currency: state.paymentCurrency,
          }

          if (state.contractBaseSalary && state.contractBaseSalary !== "") {
            salary.contract.base = Number(state.contractBaseSalary)
          }

          if (state.contractAddons && state.contractAddons.length > 0) {
            salary.contract.addons = state.contractAddons
          }
        }

        // Build b2b salary
        if (state.switchesSalary?.b2b) {
          const b2bAmounts: any = {}
          const workTypeKeys =
            state.category === "drivers" ? state.selectedWorkTypes || [] : ["all"]

          workTypeKeys.forEach((workType: string) => {
            const payment = state.b2bPayments?.[workType]
            if (payment) {
              b2bAmounts[workType] = {}
              if (payment.monthlyFrom !== undefined && payment.monthlyFrom !== "")
                b2bAmounts[workType].from = Number(payment.monthlyFrom)
              if (payment.monthlyTo !== undefined && payment.monthlyTo !== "")
                b2bAmounts[workType].to = Number(payment.monthlyTo)
              if (payment.dailyFrom !== undefined && payment.dailyFrom !== "")
                b2bAmounts[workType].dayFrom = Number(payment.dailyFrom)
              if (payment.dailyTo !== undefined && payment.dailyTo !== "")
                b2bAmounts[workType].dayTo = Number(payment.dailyTo)
            }
          })

          salary.b2b = {
            amounts: b2bAmounts,
            currency: state.paymentCurrency,
          }
        }

        obj.salary = salary

        const res = await ProfileOfferService.addOffer(obj)
        AnalyticsService.track("panel_add_offer_success", {
          plan: state?.plan,
          period: `period_${state?.period}`,
          type: state.type,
          currency: i18n.language === "pl" ? "PLN" : "EUR",
          paymentMethod: paymentMethod,
        })

        dispatch(
          updateProfile({
            status: "active",
          }),
        )

        navigate(`/transaction/status/${res.offerId}/${res.transactionId}`)
      } catch (e) {
        window.alert("Nie udało się dodać ogłoszenia! Proszę spróbować później.")
        SentryService.error("[ERROR ProfileOfferService.addOffer]:", JSON.stringify(e))
        setShowExitPrompt(true)
      } finally {
        setLoading(false)
      }
    },
    [setShowExitPrompt, state, i18n.language, dispatch, navigate],
  )

  useEffect(() => {
    AnalyticsService.track("panel_add_offer_open")
  }, [])

  useEffect(() => {
    AnalyticsService.track("panel_add_offer_step", {
      step: step,
    })
  }, [step])

  let element
  if (step === 0)
    element = <AddOfferPlan state={state} onChange={handleSubmitPlan} setType={handleSetType} />

  if (step === 1)
    element = (
      <AddOfferBasicInfo state={state} onBack={() => setStep(0)} onChange={handleSubmitBasicInfo} />
    )

  if (step === 2)
    element = (
      <AddOfferSalaryInfo
        onBack={() => setStep(1)}
        state={state}
        onChange={handleSubmitSalaryInfo}
      />
    )

  if (step === 3)
    element = (
      <AddOfferAdditionalInfo
        onBack={() => setStep(2)}
        state={state}
        onChange={handleSubmitAdditionalInfo}
      />
    )

  if (step === 4)
    element = (
      <AddOfferSummary
        onBack={() => setStep(3)}
        loading={loading}
        state={state}
        onChange={handleSubmitSummary}
      />
    )

  return <StyledComponentsProvider>{element}</StyledComponentsProvider>
}

export default AddOffer
